import { h as __h } from "preact";
import { signal } from "@preact/signals";
import { Component, h } from "preact";
import { groupResponse, orgMemberResponse } from "~/backend/validate.js";
/** @import { adminMachineResponse } from "~/backend/validate.js"; */
import { ApiError, authedApiRequest, paginatedResponse, query } from "~/lib/api.js";
import { alert } from "~/util/alert.js";
import html from "~/util/html.js";
/** @import { Placement } from "~/util/float.js"; */
import { popover } from "~/util/popover.js";
import Query from "~/util/query.js";
import Icon from "~/component/Icon.js";
import Combobox from "~/admin/component/Combobox.js";
import UserStack from "~/admin/component/UserStack.js";
import util from "~/style/util.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./setAssignee.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./setAssignee.module.css", import.meta.url);
const css = adopt(sheet);

/**
 * @param {{
 * 	anchor: HTMLButtonElement;
 * 	orgId: string;
 * 	assignee?: string | null;
 * 	placement: Placement;
 * }} props
 * @param {adminMachineResponse[]} machines
 */
export const setAssignee = async ({
  anchor,
  orgId,
  assignee,
  placement
}, ...machines) => {
  const machine_ids = machines.map(machine => machine.id);
  const removable = machines.some(machine => machine.user || machine.group);
  let res = assignee;
  if (res === undefined) {
    res = await popover({
      anchor,
      placement,
      content: (/** @type {(result?: string | null) => void} */resolve) => h(PopoverChildren, {
        resolve,
        orgId,
        removable
      })
    });
  }
  if (res === undefined) return;
  try {
    if (res === null) {
      await authedApiRequest("PUT", `/v2/orgs/${orgId}/machines/unassign`, {
        json: {
          machine_ids
        }
      });
    } else {
      await authedApiRequest("PUT", `/v2/orgs/${orgId}/machines/assign`, {
        json: {
          machine_ids,
          group_id: res.startsWith("grp_") ? res : "",
          user_id: res.startsWith("u_") ? res : ""
        }
      });
    }
    Query.invalidate([`/ui/admin/orgs/${orgId}/machines`], ...machine_ids.map(id => [`/ui/admin/orgs/${orgId}/machines/${id}`]), [`/ui/admin/orgs/${orgId}/members`], [`/v2/orgs/${orgId}/groups`]);
    return true;
  } catch (err) {
    alert(ApiError.from(err).message, {
      type: "error"
    });
    return;
  }
};

/**
 * @extends {Component<PopoverChildrenProps>}
 *
 * @typedef {object} PopoverChildrenProps
 * @property {string} orgId
 * @property {(result?: string | null) => void} resolve
 * @property {boolean} [removable]
 */
class PopoverChildren extends Component {
  query = signal("");
  members = query(() => ({
    key: [`/v2/orgs/${this.props.orgId}/members`, {
      limit: 5,
      q: this.query.value
    }],
    keepPreviousData: true
  }), {
    schema: paginatedResponse(orgMemberResponse)
  });
  groups = query(() => ({
    key: [`/v2/orgs/${this.props.orgId}/groups`, {
      limit: 5,
      q: this.query.value
    }],
    keepPreviousData: true
  }), {
    schema: paginatedResponse(groupResponse)
  });
  render() {
    const options = [...(this.groups.data?.data.map(group => ({
      id: group.id,
      label: group.name,
      render: () => __h("div", {
        "class": css.group
      }, __h(Icon, {
        name: "users",
        size: 14
      }), __h("div", {
        "class": css.text + " " + util.truncate
      }, group.name))
    })) ?? []), ...(this.members.data?.data.map(member => ({
      id: member.id,
      label: member.name,
      render: () => __h(UserStack, {
        name: member.name,
        email: member.email
      })
    })) ?? [])];
    return __h("div", {
      "class": css.popover
    }, __h("div", {
      "class": css.switcher
    }, __h("button", {
      type: "button",
      style: "--active-text: var(--color-link);",
      "data-active": true
    }, "Assign"), __h("button", {
      type: "button",
      style: "--active-text: var(--color-error-foreground);",
      disabled: !this.props.removable,
      onClick: () => this.props.resolve(null)
    }, "Remove")), h(Combobox, {
      onSelect: option => this.props.resolve(option.id),
      query: this.query,
      debounce: 200,
      loading: this.members.loading || this.groups.loading,
      options
    }));
  }
}